import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { categoryPathLabel } from '../../utils/categoryTree';
import { fill } from '../../utils/template';
import { FormCard } from '../form/FormShell';
import { TreeSelect } from '../TreeSelect';
import { DiscountTarget } from './discountDraft';
import { MenuGate } from './MenuGate';
import { Segmented } from './Segmented';
import { DiscountCatalog } from './useDiscountCatalog';

interface DiscountTargetCardProps {
  catalog: DiscountCatalog;
  target: DiscountTarget;
  categoryId: number | null;
  itemIds: number[];
  /** Позиций в цели; null — неизвестно, меню ещё не загружено. */
  targetCount: number | null;
  error?: string;
  disabled: boolean;
  onTargetChange: (target: DiscountTarget) => void;
  onCategoryChange: (categoryId: number | null) => void;
  onItemsChange: (itemIds: number[]) => void;
}

/**
 * Цель групповой скидки: раздел целиком или отдельные позиции — явным
 * переключателем, а не догадкой по тому, что выбрано последним.
 */
export function DiscountTargetCard({
  catalog,
  target,
  categoryId,
  itemIds,
  targetCount,
  error,
  disabled,
  onTargetChange,
  onCategoryChange,
  onItemsChange,
}: DiscountTargetCardProps) {
  const { t } = useLanguage();
  const strings = t.discounts;

  const category =
    categoryId != null ? catalog.categories.find((candidate) => candidate.id === categoryId) : undefined;

  const summary =
    target === 'category'
      ? category &&
        fill(strings.targetPickedCategory, {
          name: categoryPathLabel(category, catalog.categories),
          count: targetCount ?? 0,
        })
      : itemIds.length > 0 && fill(strings.targetPickedItems, { count: itemIds.length });

  return (
    <FormCard title={strings.cardTarget} hint={strings.cardTargetHint}>
      <div className="qp-form-group">
        <Segmented
          label={strings.target}
          value={target}
          options={[
            { value: 'category', label: strings.targetCategory },
            { value: 'items', label: strings.targetItems },
          ]}
          onChange={onTargetChange}
          disabled={disabled}
        />
        <span className="qp-field-hint">
          {target === 'category' ? strings.targetCategoryHint : strings.targetItemsHint}
        </span>

        <MenuGate catalog={catalog}>
          {/* Ключ по режиму: у каждого режима своё раскрытие веток. */}
          <TreeSelect
            key={target}
            categories={catalog.categories}
            items={catalog.items}
            mode={target}
            selectedCategoryId={categoryId}
            selectedItemIds={itemIds}
            onCategoryChange={onCategoryChange}
            onItemsChange={onItemsChange}
            disabled={disabled}
          />
        </MenuGate>

        {summary && <div className="qp-notice qp-notice--accent">{summary}</div>}
        {error && (
          <span className="qp-field-error" role="alert">
            {error}
          </span>
        )}
      </div>
    </FormCard>
  );
}
